import { z } from 'zod';

// Email schema
const emailSchema = z.string().email();

// Password requirements
const MIN_PASSWORD_LENGTH = 12;

// Password schema
const passwordSchema = z
  .string()
  .min(MIN_PASSWORD_LENGTH, `Password must be at least ${MIN_PASSWORD_LENGTH} characters`);

export function useValidation() {
  const validateEmail = (email: string): boolean => {
    const result = emailSchema.safeParse(email);
    return result.success;
  };

  const validatePassword = (
    password: string
  ): { valid: boolean; message: string } => {
    const result = passwordSchema.safeParse(password);

    if (!result.success) {
      return {
        valid: false,
        message: result.error.errors[0].message,
      };
    }

    // Additional strength checks
    let score = 0;
    const checks = [];

    if (/[a-z]/.test(password)) {
      score += 1;
    } else {
      checks.push('lowercase letter');
    }

    if (/[A-Z]/.test(password)) {
      score += 1;
    } else {
      checks.push('uppercase letter');
    }

    if (/\d/.test(password)) {
      score += 1;
    } else {
      checks.push('number');
    }

    if (/[^a-zA-Z0-9]/.test(password)) {
      score += 1;
    } else {
      checks.push('special character');
    }

    if (score < 3) {
      return {
        valid: false,
        message: `Password should include: ${checks.slice(0, 2).join(', ')}`,
      };
    }

    return { valid: true, message: '' };
  };

  const validateTitle = (title: string): boolean => {
    return title.trim().length > 0 && title.length <= 255;
  };

  const validateUrl = (url: string): boolean => {
    if (!url) return true; // Optional
    try {
      new URL(url.startsWith('http') ? url : `https://${url}`);
      return true;
    } catch {
      return false;
    }
  };

  const validateTOTPSecret = (secret: string): boolean => {
    // Base32 validation (A-Z, 2-7)
    const cleaned = secret.replace(/\s/g, '').toUpperCase();
    return /^[A-Z2-7]+=*$/.test(cleaned) && cleaned.length >= 16;
  };

  return {
    validateEmail,
    validatePassword,
    validateTitle,
    validateUrl,
    validateTOTPSecret,
  };
}

